import ContainerHeader from 'components/common/ContainerHeader';
import DefaultButton from 'components/common/DefaultButton';
import ScreenLayout from 'components/common/ScreenLayout';
import {
  getOperationsAnalyticsFeatureSummary,
  getOperationsAnalyticsSummary,
  getOperationsAnalyticsTrends,
} from 'function/operationsAnalyticsApi';
import {
  OperationsAnalyticsFeatureSummary,
  OperationsAnalyticsSummary,
  OperationsAnalyticsSiteMetric,
  OperationsAnalyticsTrends,
} from 'interfaces/operationsAnalytics';
import React, {useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useRecoilValue} from 'recoil';
import styled from 'styled-components';
import {colors} from 'styles/colors';
import {userInfoState} from 'utils/Atoms';
import {minmax} from 'utils/minmax';

const today = () =>
  new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);

const daysAgo = (date: string, days: number) => {
  const value = new Date(`${date}T00:00:00.000Z`);
  value.setUTCDate(value.getUTCDate() - days);
  return value.toISOString().slice(0, 10);
};
const isDate = (value: string | null) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return (
    !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
  );
};

type PeriodMode = '7days' | '30days' | 'month' | 'custom';
const APPROVAL_CRITERIA = '승인대기 현장은 집계에서 제외됩니다.';
const APPROVAL_CRITERIA_LABEL = '승인대기 현장은 제외';

const SummaryCard = ({
  label,
  value,
  accent,
  detail,
  showCriteria,
  onClick,
  disabled,
}: {
  label: string;
  value: string | number;
  accent?: string;
  detail?: string;
  showCriteria?: boolean;
  onClick?: () => void;
  disabled?: boolean;
}) => {
  const content = (
    <>
      <CardValue style={accent ? {color: accent} : undefined}>
        {value}
      </CardValue>
      <CardLabel>
        {label}
        {showCriteria && (
          <>
            <CardCriteria aria-label="현장 집계 기준" title={APPROVAL_CRITERIA}>
              ?
            </CardCriteria>
            <CriteriaText>{APPROVAL_CRITERIA_LABEL}</CriteriaText>
          </>
        )}
      </CardLabel>
      {detail && <CardHint>{detail}</CardHint>}
    </>
  );
  return onClick ? (
    <CardButton
      type="button"
      aria-label={`${label} 목록 보기`}
      onClick={onClick}
      disabled={disabled}
      title={
        disabled
          ? '과거 공사 상태 이력이 없어 최신 기준일에서만 현장목록을 확인할 수 있습니다.'
          : undefined
      }>
      {content}
    </CardButton>
  ) : (
    <Card>{content}</Card>
  );
};

const OperationsAnalytics = () => {
  const userInfo = useRecoilValue(userInfoState);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const end = today();
  const requestedStart = searchParams.get('startDate');
  const requestedEnd = searchParams.get('endDate');
  const hasRequestedPeriod =
    isDate(requestedStart) &&
    isDate(requestedEnd) &&
    requestedStart! <= requestedEnd!;
  const [period, setPeriod] = useState(
    hasRequestedPeriod
      ? {startDate: requestedStart!, endDate: requestedEnd!}
      : {startDate: daysAgo(end, 29), endDate: end},
  );
  const [periodMode, setPeriodMode] = useState<PeriodMode>(
    hasRequestedPeriod ? 'custom' : '30days',
  );
  const [summary, setSummary] = useState<OperationsAnalyticsSummary | null>(
    null,
  );
  const [trends, setTrends] = useState<OperationsAnalyticsTrends | null>(null);
  const [featureSummary, setFeatureSummary] =
    useState<OperationsAnalyticsFeatureSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({});

  const loadSummary = async (nextPeriod = period) => {
    const params = {
      userInfo,
      ...nextPeriod,
      setIsLoading,
      setErrorModal,
    };
    const [result, trendResult, featureResult] = await Promise.all([
      getOperationsAnalyticsSummary(params),
      getOperationsAnalyticsTrends(params),
      getOperationsAnalyticsFeatureSummary(params),
    ]);
    result && setSummary(result);
    trendResult && setTrends(trendResult);
    featureResult && setFeatureSummary(featureResult);
  };

  const selectPeriod = (mode: PeriodMode) => {
    setPeriodMode(mode);
    if (mode === 'custom') return;

    const nextPeriod = {
      startDate:
        mode === '7days'
          ? daysAgo(end, 6)
          : mode === 'month'
          ? `${end.slice(0, 8)}01`
          : daysAgo(end, 29),
      endDate: end,
    };
    setPeriod(nextPeriod);
    loadSummary(nextPeriod);
  };

  useEffect(() => {
    loadSummary();
  }, []);

  const number = (value: number) => value.toLocaleString('ko-KR');
  const rate =
    summary?.usage.activeRate == null
      ? '산출 불가'
      : `${(summary.usage.activeRate * 100).toFixed(1)}%`;
  const openSites = (metric: OperationsAnalyticsSiteMetric) => {
    const query = new URLSearchParams({
      metric,
      startDate: summary?.period.startDate ?? period.startDate,
      endDate: summary?.period.endDate ?? period.endDate,
    });
    navigate(`/operationsAnalytics/sites?${query.toString()}`);
  };
  const stateListDisabled = summary?.period.endDate !== end;

  return (
    <ScreenLayout isLoading={isLoading} errorModal={errorModal}>
      <Container>
        <ContainerHeader mainTitle="operationsAnalytics" />

        <Inner>
          <PageTitle>운영 현황 분석</PageTitle>

          <FilterBar aria-label="운영 현황 조회 기간">
            <PeriodWrap>
              <PeriodLabel>조회 기간</PeriodLabel>
              <PeriodButton
                type="button"
                $active={periodMode === '7days' ? 1 : 0}
                onClick={() => selectPeriod('7days')}>
                최근 7일
              </PeriodButton>
              <PeriodButton
                type="button"
                $active={periodMode === '30days' ? 1 : 0}
                onClick={() => selectPeriod('30days')}>
                최근 30일
              </PeriodButton>
              <PeriodButton
                type="button"
                $active={periodMode === 'month' ? 1 : 0}
                onClick={() => selectPeriod('month')}>
                이번 달
              </PeriodButton>
              <PeriodButton
                type="button"
                $active={periodMode === 'custom' ? 1 : 0}
                onClick={() => selectPeriod('custom')}>
                사용자 지정
              </PeriodButton>
            </PeriodWrap>

            <CustomPeriod>
              <DateInput
                aria-label="시작일"
                type="date"
                value={period.startDate}
                max={period.endDate}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setPeriodMode('custom');
                  setPeriod({...period, startDate: event.target.value});
                }}
              />
              <span>~</span>
              <DateInput
                aria-label="종료일"
                type="date"
                value={period.endDate}
                min={period.startDate}
                max={end}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setPeriodMode('custom');
                  setPeriod({...period, endDate: event.target.value});
                }}
              />
              <DefaultButton
                text="toSearch"
                actived={1}
                buttonStyle={{
                  width: minmax(90),
                  height: minmax(40),
                  borderRadius: minmax(6),
                  backgroundColor: colors.primary2,
                }}
                onClick={() => loadSummary()}
              />
            </CustomPeriod>
          </FilterBar>

          {summary && (
            <>
              <ResultMeta>
                실시간 원본 집계 · 상태 지표 기준일{' '}
                {summary.currentStateAsOfDate} · 최종 조회{' '}
                {new Date(summary.calculatedAt).toLocaleString('ko-KR', {
                  timeZone: 'Asia/Seoul',
                })}{' '}
                · 공사 상태는 현재 값 기준
              </ResultMeta>

              <CardGrid>
                <SummaryCard
                  label="운영 현장"
                  value={number(summary.usage.operatingSiteCount)}
                  showCriteria
                  onClick={() => openSites('operating')}
                  disabled={stateListDisabled}
                />
                <SummaryCard
                  label="활성 현장"
                  value={number(summary.usage.activeSiteCount)}
                  accent={colors.darkLimeGreen}
                  detail={rate}
                  onClick={() => openSites('active')}
                  disabled={stateListDisabled}
                />
                <SummaryCard
                  label="저활성 현장"
                  value={number(summary.usage.lowActivitySiteCount)}
                  accent="#e65100"
                  onClick={() => openSites('lowActivity')}
                  disabled={stateListDisabled}
                />
                <SummaryCard
                  label="휴면 현장"
                  value={number(summary.usage.dormantSiteCount)}
                  accent={colors.primary}
                  onClick={() => openSites('dormant')}
                  disabled={stateListDisabled}
                />
                <SummaryCard
                  label="미착수 현장"
                  value={number(summary.usage.notStartedSiteCount)}
                  onClick={() => openSites('notStarted')}
                  disabled={stateListDisabled}
                />
                <SummaryCard
                  label="기간 내 신규 현장"
                  value={number(summary.sites.newSiteCount)}
                  onClick={() => openSites('new')}
                />
                <SummaryCard
                  label={`일평균 승인 출근 · ${summary.period.calendarDays}일`}
                  value={number(summary.attendance.averageDailyApprovedUsers)}
                  accent={colors.primary2}
                />
                <SummaryCard
                  label="전체 승인 출근"
                  value={number(summary.attendance.approvedUserDays)}
                />
                <SummaryCard
                  label="종료 임박 D-30"
                  value={number(
                    summary.construction.endingWithin30DaysSiteCount,
                  )}
                  accent={colors.primary}
                  onClick={() => openSites('endingD30')}
                  disabled={stateListDisabled}
                />
              </CardGrid>

              <SectionTitle>
                현장 상태 분포
                <CriteriaButton
                  type="button"
                  aria-label="현장 집계 기준"
                  title={APPROVAL_CRITERIA}>
                  ?
                </CriteriaButton>
                <CriteriaText>{APPROVAL_CRITERIA_LABEL}</CriteriaText>
              </SectionTitle>
              <DataTable>
                <thead>
                  <tr>
                    <th>분류</th>
                    <th>상태</th>
                    <th>현재 현장 수</th>
                    <th>판정 기준</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td rowSpan={4}>공사 상태</td>
                    <td>착수 전</td>
                    <td>{number(summary.construction.notStartedSiteCount)}</td>
                    <td>
                      TongPlace 현재 상태 (공사 시작 전 또는 공사기간 미설정)
                    </td>
                  </tr>
                  <tr>
                    <td>운영 중</td>
                    <td>{number(summary.construction.operatingSiteCount)}</td>
                    <td>TongPlace 현재 상태 (등록된 공사기간 내 진행 중)</td>
                  </tr>
                  <tr>
                    <td>중단</td>
                    <td>{number(summary.construction.stoppedSiteCount)}</td>
                    <td>TongPlace 현재 상태 (중단 요청으로 운영 정지)</td>
                  </tr>
                  <tr>
                    <td>완료</td>
                    <td>{number(summary.construction.completedSiteCount)}</td>
                    <td>TongPlace 현재 상태 (공사 종료일 경과)</td>
                  </tr>
                  <tr>
                    <td rowSpan={4}>사용 상태</td>
                    <td>활성</td>
                    <td>{number(summary.usage.activeSiteCount)}</td>
                    <td>최근 7일 승인 출근</td>
                  </tr>
                  <tr>
                    <td>저활성</td>
                    <td>{number(summary.usage.lowActivitySiteCount)}</td>
                    <td>마지막 승인 출근 7~27일 전</td>
                  </tr>
                  <tr>
                    <td>휴면</td>
                    <td>{number(summary.usage.dormantSiteCount)}</td>
                    <td>마지막 승인 출근 28일 이상</td>
                  </tr>
                  <tr>
                    <td>미착수</td>
                    <td>{number(summary.usage.notStartedSiteCount)}</td>
                    <td>착수 기준일 경과 후 승인 출근 없음</td>
                  </tr>
                </tbody>
              </DataTable>

              {featureSummary && (
                <>
                  <SectionTitle>전체 기능 통계</SectionTitle>
                  <DataTable>
                    <thead>
                      <tr>
                        <th>기능</th>
                        <th>집계</th>
                        <th>현장·처리 현황</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>승인 출근</td>
                        <td>
                          {number(featureSummary.attendance.approvedUserDays)}명
                        </td>
                        <td>
                          {number(featureSummary.attendance.siteCount)}개 현장
                        </td>
                      </tr>
                      <tr>
                        <td>안전교육 문서</td>
                        <td>
                          {number(featureSummary.safetyEducation.documentCount)}
                          건
                        </td>
                        <td>
                          {number(featureSummary.safetyEducation.siteCount)}개
                          현장
                        </td>
                      </tr>
                      <tr>
                        <td>위험성평가</td>
                        <td>
                          {number(featureSummary.riskAssessment.documentCount)}
                          건
                        </td>
                        <td>
                          {number(featureSummary.riskAssessment.siteCount)}개
                          현장
                        </td>
                      </tr>
                      <tr>
                        <td>TBM</td>
                        <td>{number(featureSummary.tbm.documentCount)}건</td>
                        <td>{number(featureSummary.tbm.siteCount)}개 현장</td>
                      </tr>
                      <tr>
                        <td>작업계획서</td>
                        <td>
                          {number(featureSummary.workPlan.documentCount)}건
                        </td>
                        <td>
                          {number(featureSummary.workPlan.siteCount)}개 현장
                        </td>
                      </tr>
                      <tr>
                        <td>의견청취</td>
                        <td>
                          {number(featureSummary.employeeFeedback.totalCount)}건
                        </td>
                        <td>
                          처리{' '}
                          {number(
                            featureSummary.employeeFeedback.processedCount,
                          )}
                          건 ·{' '}
                          {number(featureSummary.employeeFeedback.siteCount)}개
                          현장
                        </td>
                      </tr>
                      <tr>
                        <td>일반 전달사항</td>
                        <td>
                          {number(
                            featureSummary.announcements
                              .contractorAnnouncementCount,
                          )}
                          건
                        </td>
                        <td>별도 집계</td>
                      </tr>
                      <tr>
                        <td>현장공지</td>
                        <td>
                          {number(
                            featureSummary.announcements
                              .tongPlaceAnnouncementCount,
                          )}
                          건
                        </td>
                        <td>별도 집계</td>
                      </tr>
                    </tbody>
                  </DataTable>
                  <FeatureCriteria>
                    승인대기 현장은 제외 · 일반 전달사항과 현장공지는 별도 집계
                    · 본사 전달사항 제외
                  </FeatureCriteria>
                </>
              )}

              <SectionTitle>기간별 추이</SectionTitle>
              {trends?.available ? (
                <DataTable>
                  <thead>
                    <tr>
                      <th>기준일</th>
                      <th>운영 현장</th>
                      <th>활성</th>
                      <th>저활성</th>
                      <th>휴면</th>
                      <th>미착수</th>
                      <th>승인 출근</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trends.items.map(item => (
                      <tr key={item.snapshotDate}>
                        <td>{item.snapshotDate}</td>
                        <td>{number(item.operatingSiteCount)}</td>
                        <td>{number(item.activeSiteCount)}</td>
                        <td>{number(item.lowActivitySiteCount)}</td>
                        <td>{number(item.dormantSiteCount)}</td>
                        <td>{number(item.usageNotStartedSiteCount)}</td>
                        <td>{number(item.approvedUserCount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
              ) : (
                <EmptyResult>
                  <strong>집계 개시일 이전 데이터 없음</strong>
                  <span>일별 스냅샷이 생성된 날짜부터 추이를 표시합니다.</span>
                </EmptyResult>
              )}

              <SectionTitle>조치 대상 요약</SectionTitle>
              <DataTable>
                <thead>
                  <tr>
                    <th>조치 유형</th>
                    <th>대상 현장 수</th>
                    <th>판정 기준</th>
                    <th>조회 상태</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>미착수</td>
                    <td>{number(summary.usage.notStartedSiteCount)}</td>
                    <td>착수 기준일 경과 후 승인 출근 없음</td>
                    <td>
                      <StatusBadge>실시간</StatusBadge>
                    </td>
                  </tr>
                  <tr>
                    <td>휴면</td>
                    <td>{number(summary.usage.dormantSiteCount)}</td>
                    <td>최근 28일 승인 출근 없음</td>
                    <td>
                      <StatusBadge>실시간</StatusBadge>
                    </td>
                  </tr>
                  <tr>
                    <td>종료 임박</td>
                    <td>
                      {number(summary.construction.endingWithin30DaysSiteCount)}
                    </td>
                    <td>공사 종료일 D-30 이내</td>
                    <td>
                      <StatusBadge>실시간</StatusBadge>
                    </td>
                  </tr>
                </tbody>
              </DataTable>
            </>
          )}
        </Inner>
      </Container>
    </ScreenLayout>
  );
};

export default OperationsAnalytics;

const Container = styled.div`
  display: flex;
  width: 100%;
  flex: 1;
  flex-direction: column;
  margin-bottom: ${minmax(50)};
`;

const Inner = styled.main`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${minmax(20)};
`;

const PageTitle = styled.h1`
  width: fit-content;
  margin-bottom: ${minmax(20)};
  border-bottom: 3px solid ${colors.primary};
  color: ${colors.black};
  font-size: ${minmax(25)};
  font-weight: 700;
`;

const FilterBar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${minmax(20)};
  flex-wrap: wrap;
  margin-bottom: ${minmax(10)};
`;

const PeriodWrap = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${minmax(10)};
`;

const PeriodLabel = styled.span`
  margin-right: ${minmax(4)};
  color: ${colors.black};
  font-size: ${minmax(18)};
  font-weight: 600;
`;

const PeriodButton = styled.button<{$active: number}>`
  padding: ${minmax(8)} ${minmax(18)};
  border: 1px solid
    ${props => (props.$active ? colors.primary2 : colors.lightGrey)};
  border-radius: ${minmax(6)};
  background-color: ${props =>
    props.$active ? colors.primary2 : colors.white};
  color: ${props => (props.$active ? colors.white : colors.darkGray)};
  font-size: ${minmax(16)};
  cursor: pointer;
`;

const CustomPeriod = styled.div`
  display: flex;
  align-items: center;
  gap: ${minmax(8)};
`;

const DateInput = styled.input`
  height: ${minmax(40)};
  padding: 0 ${minmax(10)};
  border: 1px solid ${colors.lightGrey};
  border-radius: ${minmax(6)};
  color: ${colors.black};
  font-size: ${minmax(16)};
`;

const ResultMeta = styled.div`
  margin-bottom: ${minmax(14)};
  color: ${colors.graniteGray};
  font-size: ${minmax(16)};
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${minmax(14)};
  margin-bottom: ${minmax(30)};
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${minmax(18)} ${minmax(10)};
  border: 1px solid ${colors.lightGrey};
  border-radius: ${minmax(8)};
  background-color: ${colors.whiteSmoke};
`;

const CardButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: ${minmax(18)} ${minmax(10)};
  border: 1px solid ${colors.lightGrey};
  border-radius: ${minmax(8)};
  background-color: ${colors.whiteSmoke};
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${colors.primary2};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

const CardValue = styled.span`
  color: ${colors.black};
  font-size: ${minmax(30)};
  font-weight: 700;
`;

const CardLabel = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: ${minmax(6)};
  color: ${colors.graniteGray};
  font-size: ${minmax(16)};
  text-align: center;
`;

const CardHint = styled.span`
  margin-top: ${minmax(2)};
  color: ${colors.graniteGray};
  font-size: ${minmax(14)};
`;

const CriteriaButton = styled.button`
  width: ${minmax(17)};
  height: ${minmax(17)};
  margin-left: ${minmax(5)};
  padding: 0;
  border: 1px solid ${colors.graniteGray};
  border-radius: 50%;
  background-color: ${colors.white};
  color: ${colors.graniteGray};
  font-size: ${minmax(12)};
  font-weight: 700;
  line-height: ${minmax(15)};
  cursor: help;
`;

const CardCriteria = styled.span`
  width: ${minmax(17)};
  height: ${minmax(17)};
  margin-left: ${minmax(5)};
  border: 1px solid ${colors.graniteGray};
  border-radius: 50%;
  background-color: ${colors.white};
  color: ${colors.graniteGray};
  font-size: ${minmax(12)};
  font-weight: 700;
  line-height: ${minmax(15)};
`;

const CriteriaText = styled.span`
  margin-left: ${minmax(5)};
  color: ${colors.graniteGray};
  font-size: ${minmax(13)};
  font-weight: 400;
  white-space: nowrap;
`;

const SectionTitle = styled.h2`
  width: fit-content;
  margin: ${minmax(10)} 0 ${minmax(14)};
  border-bottom: 3px solid ${colors.primary};
  color: ${colors.black};
  font-size: ${minmax(22)};
  font-weight: 700;
`;

const DataTable = styled.table`
  width: 100%;
  margin-bottom: ${minmax(30)};
  border-collapse: collapse;
  border-bottom: 1px solid ${colors.darkGray};

  th {
    height: ${minmax(52)};
    padding: ${minmax(10)};
    background-color: #f7f7f7;
    color: ${colors.graniteGray};
    font-size: ${minmax(18)};
    font-weight: 600;
    text-align: center;
  }

  td {
    height: ${minmax(52)};
    padding: ${minmax(10)};
    border-top: 1px solid ${colors.lightGrey};
    color: ${colors.black};
    font-size: ${minmax(17)};
    text-align: center;
  }
`;

const EmptyResult = styled.div`
  min-height: ${minmax(150)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${minmax(8)};
  margin-bottom: ${minmax(30)};
  border-top: 1px solid ${colors.lightGrey};
  border-bottom: 1px solid ${colors.darkGray};
  color: ${colors.graniteGray};
  text-align: center;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: ${minmax(3)} ${minmax(10)};
  border-radius: ${minmax(10)};
  background-color: #e8f5e9;
  color: ${colors.darkLimeGreen};
  font-size: ${minmax(15)};
  font-weight: 600;
`;

const FeatureCriteria = styled.p`
  margin: ${minmax(-20)} 0 ${minmax(30)};
  color: ${colors.graniteGray};
  font-size: ${minmax(14)};
  text-align: right;
`;
